// qs-ColorsPanel.vue
<template>
  <div class="colors-panel">
    <div class="color-swatches">
      <button
        v-for="(color, index) in colors"
        :key="`color-${index}`"
        class="color-button"
        :class="{ active: selectedColor === color.color }"
        @click="selectColor(color)"
        @contextmenu="openContextMenu($event, color, index)"
        @mousedown="startLongPress($event, color, index)"
        @mouseup="endLongPress"
        @mouseleave="handleMouseLeave"
        @touchstart.passive="startLongPress($event, color, index)"
        @touchend.passive="endLongPress"
        @touchcancel.passive="endLongPress"
      >
        <div class="color-swatch">
          <div
            class="color-circle"
            :style="{
              backgroundColor: color.color,
              opacity: color.opacity / 100
            }"
          />
        </div>
      </button>
    </div>
    <div
      v-if="showContextMenu"
      class="integrated-color-picker"
    >
      <ColorPicker
        :model-value="contextMenuColor"
        :opacity="contextMenuOpacity"
        @update:model-value="updateContextColor"
        @update:opacity="updateContextOpacity"
        @apply="applyAndClose"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import ColorPicker from '@/components/ui/qs-ColorPicker.vue'
import { useToolStore } from '@/stores/qs-store-tools'
import { cursorManager } from '@/utils/qs-cursorManager'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

type ColorItem = { color: string; opacity: number }

interface Props {
  initialColors?: ColorItem[]
  selectedColor?: string
}

const props = withDefaults(defineProps<Props>(), {
  initialColors: () => [
    { color: '#f8f8f8', opacity: 100 },
    { color: '#1a1a1a', opacity: 100 },
    { color: '#e74c3c', opacity: 100 },
    { color: '#3498db', opacity: 100 },
    { color: '#2ecc71', opacity: 100 },
    { color: '#f39c12', opacity: 100 },
    { color: '#9b59b6', opacity: 100 }
  ],
  selectedColor: '#3498db'
})

const emit = defineEmits<{
  'color-selected': [color: string, opacity: number]
  'color-edited': [index: number, color: string, opacity: number]
  'panel-hover': [isHovered: boolean]
}>()

const toolStore = useToolStore()

const colors = ref<ColorItem[]>([...props.initialColors])
const selectedColor = ref(props.selectedColor)
const showContextMenu = ref(false)
const contextMenuColor = ref('#000000')
const contextMenuOpacity = ref(100)
const contextMenuColorIndex = ref(-1)
const hasLongPressed = ref(false)

let longPressTimer: ReturnType<typeof setTimeout> | null = null
const longPressDelay = 500

const selectedColorObj = computed(() => {
  return colors.value.find(c => c.color === selectedColor.value) ?? colors.value[0]
})

watch(() => toolStore.currentTool.color, (newColor) => {
  if (newColor !== selectedColor.value) {
    selectedColor.value = newColor
  }
}, { immediate: true })

const selectColor = (colorObj: ColorItem) => {
  if (showContextMenu.value) return
  selectedColor.value = colorObj.color
  toolStore.setColor(colorObj.color)
  toolStore.setOpacity(colorObj.opacity / 100)
  emit('color-selected', colorObj.color, colorObj.opacity)
}

const startLongPress = (event: MouseEvent | TouchEvent, colorObj: ColorItem, index: number) => {
  if (event instanceof MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }
  if (showContextMenu.value) return
  if (longPressTimer) {
    clearTimeout(longPressTimer)
  }
  longPressTimer = setTimeout(() => {
    if (!showContextMenu.value) {
      hasLongPressed.value = true
      openContextMenu(event, colorObj, index)
    }
  }, longPressDelay)
}

const endLongPress = () => {
  if (longPressTimer && !hasLongPressed.value) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

const handleMouseLeave = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target?.classList.contains('color-button')) {
    target.blur()
  }
}

const openContextMenu = (event: MouseEvent | TouchEvent, colorObj: ColorItem, colorIndex: number) => {
  event.preventDefault()
  event.stopPropagation()
  showContextMenu.value = true
  contextMenuColor.value = colorObj.color
  contextMenuOpacity.value = colorObj.opacity
  contextMenuColorIndex.value = colorIndex
  emit('panel-hover', true)
}

const closeContextMenu = () => {
  showContextMenu.value = false
  hasLongPressed.value = false
  contextMenuColorIndex.value = -1
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  emit('panel-hover', false)
}

const updateContextColor = (color: string) => {
  contextMenuColor.value = color
  if (contextMenuColorIndex.value >= 0 && contextMenuColorIndex.value < colors.value.length) {
    colors.value[contextMenuColorIndex.value].color = color
  }
}

const updateContextOpacity = (opacity: number) => {
  contextMenuOpacity.value = opacity
  if (contextMenuColorIndex.value >= 0 && contextMenuColorIndex.value < colors.value.length) {
    colors.value[contextMenuColorIndex.value].opacity = opacity
  }
}

const applyAndClose = () => {
  selectedColor.value = contextMenuColor.value
  toolStore.setColor(contextMenuColor.value)
  toolStore.setOpacity(contextMenuOpacity.value / 100)
  emit('color-selected', contextMenuColor.value, contextMenuOpacity.value)
  if (contextMenuColorIndex.value >= 0 && contextMenuColorIndex.value < colors.value.length) {
    colors.value[contextMenuColorIndex.value].color = contextMenuColor.value
    colors.value[contextMenuColorIndex.value].opacity = contextMenuOpacity.value
    emit('color-edited', contextMenuColorIndex.value, contextMenuColor.value, contextMenuOpacity.value)
  }
  closeContextMenu()
  window.dispatchEvent(new CustomEvent('panel-content-changed', {
    detail: { panelId: 'colors' }
  }))
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (cursorManager.isAnyInputActive()) return
  if (event.key === 'Escape' && showContextMenu.value) {
    closeContextMenu()
  }
}

const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (showContextMenu.value && !target.closest('.colors-panel')) {
    closeContextMenu()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  document.addEventListener('click', handleClickOutside)
  emit('color-selected', selectedColor.value, selectedColorObj.value.opacity)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
  document.removeEventListener('click', handleClickOutside)
  if (longPressTimer) {
    clearTimeout(longPressTimer)
  }
})
</script>

<style scoped>
.colors-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: auto;
  min-width: fit-content;
  max-width: none;
  min-height: 44px;
  padding: 0;
  background: transparent;
  box-sizing: border-box;
}

.color-swatches {
  display: flex;
  width: 100%;
  height: 44px;
}

.integrated-color-picker {
  width: 100%;
  margin-top: 0;
  background: transparent;
}

.color-button {
  width: 44px;
  height: 44px;
  min-width: 44px;
  max-width: 44px;
  min-height: 44px;
  max-height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  outline: none;
}

.color-button:hover:not(:focus) {
  background: theme('colors.creme.DEFAULT');
  border-radius: theme('borderRadius.DEFAULT');
}

.color-button:focus {
  background: theme('colors.creme.dark');
  outline: none;
  border-radius: theme('borderRadius.DEFAULT');
}

.color-button.active {
  background: theme('colors.creme.lighter');
  border-radius: theme('borderRadius.DEFAULT');
}

.color-swatch {
  width: 44px;
  height: 44px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0;
  flex-shrink: 0;
  box-sizing: border-box;
  position: relative;
}

.color-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none !important;
  outline: none !important;
}
</style>